import { z } from "zod";
import {
  adminTaskOccurrenceSchema,
  clinicVisitActivityOccurrenceSchema,
  groupOfClinicVisitActivityOccurrenceSchema,
  groupOfHomeVisitActivityOccurrenceSchema,
  homeVisitActivityOccurrenceSchema,
  patientMeasurementTaskActivityOccurrenceSchema,
  patientTaskActivityOccurrenceSchema,
  phoneCallActivityOccurrenceSchema,
  unplannedClinicVisitGroupSchema,
  unplannedHomeVisitGroupSchema,
  videoActivityOccurrenceSchema,
} from "./activities";

// An activity occurrence as the logistics domain holds it. Every field of an
// occurrence originates there except the patient, which lives in a separate
// host: logistics knows only an id, and the two are joined in the client by the
// enrich functions in each app's api/Activities. So these schemas are the
// occurrence schemas with that one field reduced to the reference logistics
// actually stores — which is also why they are derived from those and not the
// other way round.
//
// Parse a response from the logistics host with these; hand the app the
// enriched shape from `./activities`. A type from here reaching a component is
// a sign the enrichment was skipped.

const patient = { patient: true } as const;
const requiredPatientId = { patientId: z.uuid() };
const optionalPatientId = { patientId: z.uuid().nullish() };

export const unenrichedHomeVisitActivityOccurrenceSchema =
  homeVisitActivityOccurrenceSchema.omit(patient).extend(requiredPatientId);
export type IUnenrichedHomeVisitActivityOccurrence = z.infer<
  typeof unenrichedHomeVisitActivityOccurrenceSchema
>;

// The group the same way: its occurrences lose the patient along with it, since
// the host that served the group did not know any of them either.
export const unenrichedGroupOfHomeVisitActivityOccurrenceSchema =
  groupOfHomeVisitActivityOccurrenceSchema.omit(patient).extend({
    ...requiredPatientId,
    occurrences: z.array(unenrichedHomeVisitActivityOccurrenceSchema),
  });
export type IUnenrichedGroupOfHomeVisitActivityOccurrence = z.infer<
  typeof unenrichedGroupOfHomeVisitActivityOccurrenceSchema
>;

export const unenrichedVideoActivityOccurrenceSchema =
  videoActivityOccurrenceSchema.omit(patient).extend(requiredPatientId);

export const unenrichedClinicVisitActivityOccurrenceSchema =
  clinicVisitActivityOccurrenceSchema.omit(patient).extend(requiredPatientId);

export const unenrichedGroupOfClinicVisitActivityOccurrenceSchema =
  groupOfClinicVisitActivityOccurrenceSchema.omit(patient).extend({
    ...requiredPatientId,
    occurrences: z.array(unenrichedClinicVisitActivityOccurrenceSchema),
  });
export type IUnenrichedGroupOfClinicVisitActivityOccurrence = z.infer<
  typeof unenrichedGroupOfClinicVisitActivityOccurrenceSchema
>;

export const unenrichedGroupOfActivityOccurrenceSchema = z.discriminatedUnion(
  "category",
  [
    unenrichedGroupOfHomeVisitActivityOccurrenceSchema,
    unenrichedGroupOfClinicVisitActivityOccurrenceSchema,
  ],
);
export type IUnenrichedGroupOfActivityOccurrence = z.infer<
  typeof unenrichedGroupOfActivityOccurrenceSchema
>;

const unenrichedPhoneCallActivityOccurrenceSchema =
  phoneCallActivityOccurrenceSchema.omit(patient).extend(requiredPatientId);

export const unenrichedPatientTaskActivityOccurrenceSchema =
  patientTaskActivityOccurrenceSchema.omit(patient).extend(requiredPatientId);

export const unenrichedAdminTaskOccurrenceSchema = adminTaskOccurrenceSchema
  .omit(patient)
  .extend(optionalPatientId);

export const unenrichedPatientMeasurementTaskActivityOccurrenceSchema =
  patientMeasurementTaskActivityOccurrenceSchema
    .omit(patient)
    .extend(requiredPatientId);

const unenrichedNonRoutableEmployeePerformedActivityOccurrenceSchema =
  z.discriminatedUnion("category", [
    unenrichedVideoActivityOccurrenceSchema,
    unenrichedClinicVisitActivityOccurrenceSchema,
    unenrichedAdminTaskOccurrenceSchema,
    unenrichedPhoneCallActivityOccurrenceSchema,
  ]);

// Non-routable work can appear directly on a shift either as a standalone
// occurrence or, for groupable clinic visits, as a group.
export const unenrichedNonRoutableEmployeePerformedActivityOccurrenceOrGroupSchema =
  z.union([
    unenrichedNonRoutableEmployeePerformedActivityOccurrenceSchema,
    unenrichedGroupOfClinicVisitActivityOccurrenceSchema,
  ]);

export const unenrichedActivityOccurrenceSchema = z.discriminatedUnion(
  "category",
  [
    unenrichedHomeVisitActivityOccurrenceSchema,
    unenrichedVideoActivityOccurrenceSchema,
    unenrichedClinicVisitActivityOccurrenceSchema,
    unenrichedPatientTaskActivityOccurrenceSchema,
    unenrichedAdminTaskOccurrenceSchema,
    unenrichedPatientMeasurementTaskActivityOccurrenceSchema,
    unenrichedPhoneCallActivityOccurrenceSchema,
  ],
);
export type IUnenrichedActivityOccurrence = z.infer<
  typeof unenrichedActivityOccurrenceSchema
>;

// Not discriminated, for the same reason as in ./activities.
export const unenrichedActivityOccurrenceOrGroupSchema = z.union([
  unenrichedActivityOccurrenceSchema,
  unenrichedGroupOfActivityOccurrenceSchema,
]);
export type IUnenrichedActivityOccurrenceOrGroup = z.infer<
  typeof unenrichedActivityOccurrenceOrGroupSchema
>;

const unenrichedAdminTaskOccurrenceWithRequiredPatientIdSchema =
  unenrichedAdminTaskOccurrenceSchema.extend(requiredPatientId);

// The same seven categories, for the endpoints that always send a patient id:
// AdminTask is the only category whose id is optional, so it is the only member
// that differs from unenrichedActivityOccurrenceSchema.
const unenrichedActivityOccurrenceWithRequiredPatientIdSchema =
  z.discriminatedUnion("category", [
    unenrichedHomeVisitActivityOccurrenceSchema,
    unenrichedVideoActivityOccurrenceSchema,
    unenrichedClinicVisitActivityOccurrenceSchema,
    unenrichedPatientTaskActivityOccurrenceSchema,
    unenrichedAdminTaskOccurrenceWithRequiredPatientIdSchema,
    unenrichedPatientMeasurementTaskActivityOccurrenceSchema,
    unenrichedPhoneCallActivityOccurrenceSchema,
  ]);

const unenrichedActivityOccurrenceOrGroupWithRequiredPatientIdSchema = z.union([
  unenrichedActivityOccurrenceWithRequiredPatientIdSchema,
  unenrichedGroupOfActivityOccurrenceSchema,
]);

export const unenrichedActivityOccurrencesAndGroupsSchema = z.array(
  unenrichedActivityOccurrenceOrGroupSchema,
);

export const unenrichedActivityOccurrencesAndGroupsWithRequiredPatientIdsSchema =
  z.array(unenrichedActivityOccurrenceOrGroupWithRequiredPatientIdSchema);

// The unplanned list reads a group's status as narrowly as ./activities does.
export const unenrichedUnplannedGroupSchema = z.union([
  unenrichedGroupOfHomeVisitActivityOccurrenceSchema.extend({
    visitStatus: unplannedHomeVisitGroupSchema.shape.visitStatus,
  }),
  unenrichedGroupOfClinicVisitActivityOccurrenceSchema.extend({
    visitStatus: unplannedClinicVisitGroupSchema.shape.visitStatus,
  }),
]);

export const unenrichedUnplannedOccurrencesAndGroupsSchema = z.array(
  z.union([
    unenrichedUnplannedGroupSchema,
    ...unenrichedActivityOccurrenceSchema.options,
  ]),
);
